import { loadBaseCSS } from '../shared/button-utils.js';
import '../action-panel-items-editor/qds-action-panel-items-editor.js';
import '../card-grid-editor/qds-card-grid-editor.js';
import '../property-editor/qds-property-editor.js';
import {
  escapeHtml,
  findBlockById,
  getContentBlockDisplayName,
  getDefinitionField,
  normalizeContentDocument,
  resolveRegistry
} from '../content-blocks/qds-content-blocks.js';
import type { QdsContentBreakpoint, QdsContentDocument } from '../../types/content.js';
import type { QdsBlockDefinition, QdsBlockRegistry, QdsConfigField } from '../../types/blocks.js';
import type { QdsDesignerContext } from '../../types/designer.js';

type EditorTarget = Record<string, any>;
type ConfigValueKind = 'primitive' | 'json' | 'structured';

const BREAKPOINTS: QdsContentBreakpoint[] = ['desktop', 'tablet', 'mobile'];

function cloneStructuredValue<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * @element qds-content-properties
 *
 * The properties inspector panel that surfaces editors for document, layout, and canvas settings plus the selected block's metadata, layout, and configuration, delegating to host-supplied editors when available.
 *
 * @attr {boolean} readonly - When present, renders a read-only JSON view instead of editable property editors
 * @attr {string} selected-block-id - Id of the block whose properties are shown
 *
 * @property {QdsContentDocument | null} document - The content document being inspected
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {QdsDesignerContext | undefined} context - Host-supplied designer context (registry, capabilities)
 * @property {string | null} selectedBlockId - Id of the currently selected block
 * @property {boolean} readonly - Whether the panel is in read-only mode
 *
 * @fires document-change - Fired when document-level settings change
 * @fires document-layout-change - Fired when document layout settings change
 * @fires document-canvas-change - Fired when canvas settings change
 * @fires block-meta-change - Fired when the selected block's metadata (title, visibility) changes
 * @fires block-layout-change - Fired when the selected block's layout changes
 * @fires block-config-change - Fired when the selected block's configuration changes
 */
export class QdsContentProperties extends HTMLElement {
  private _document: QdsContentDocument | null = null;
  private _registry: QdsBlockRegistry | null = null;
  private _context?: QdsDesignerContext;
  private _selectedBlockId: string | null = null;
  private _readonly = false;
  private _configParseError = '';
  private _configValueKinds: Record<string, ConfigValueKind> = {};

  static get observedAttributes(): string[] {
    return ['readonly', 'selected-block-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('change', this._handleChange as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('change', this._handleChange as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    if (name === 'selected-block-id') {
      this._selectedBlockId = newValue;
    }

    this.render();
  }

  get document(): QdsContentDocument | null {
    return this._document;
  }

  set document(value: QdsContentDocument | null) {
    this._document = value;
    this.render();
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get context(): QdsDesignerContext | undefined {
    return this._context;
  }

  set context(value: QdsDesignerContext | undefined) {
    this._context = value;
    this.render();
  }

  get selectedBlockId(): string | null {
    return this._selectedBlockId;
  }

  set selectedBlockId(value: string | null) {
    this._selectedBlockId = value;
    if (value) {
      this.setAttribute('selected-block-id', value);
    } else {
      this.removeAttribute('selected-block-id');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _dispatch(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  private _selectedBlock(documentModel: QdsContentDocument) {
    return this._selectedBlockId
      ? findBlockById(documentModel.blocks, this._selectedBlockId, 'page')
      : null;
  }

  private _createDocumentTarget(documentModel: QdsContentDocument): EditorTarget {
    return {
      title: documentModel.title || '',
      summary: documentModel.summary || '',
      bodyHtml: documentModel.bodyHtml || '',
      mode: documentModel.mode
    };
  }

  private _createDocumentMetadata(): Record<string, QdsConfigField> {
    return {
      title: { displayName: 'Title', type: 'string' },
      summary: { displayName: 'Summary', type: 'textarea' },
      bodyHtml: { displayName: 'Body HTML', type: 'textarea' },
      mode: {
        displayName: 'Mode',
        type: 'select',
        options: [
          { value: 'page', label: 'Page' },
          { value: 'dashboard', label: 'Dashboard' }
        ]
      }
    };
  }

  private _createLayoutTarget(documentModel: QdsContentDocument): EditorTarget {
    return {
      columns: documentModel.layout?.columns || 12,
      rowHeight: documentModel.layout?.rowHeight || 128,
      gap: documentModel.layout?.gap || '16px',
      maxWidth: documentModel.layout?.maxWidth || 'min(1400px, 100%)'
    };
  }

  private _createLayoutMetadata(): Record<string, QdsConfigField> {
    return {
      columns: { displayName: 'Columns', type: 'number', min: 1, max: 12, step: 1 },
      rowHeight: { displayName: 'Row Height', type: 'number', min: 64, max: 320, step: 8 },
      gap: { displayName: 'Gap', type: 'string' },
      maxWidth: { displayName: 'Max Width', type: 'string' }
    };
  }

  private _createCanvasTarget(documentModel: QdsContentDocument): EditorTarget {
    return {
      zoom: documentModel.canvas?.zoom || 100,
      snapToGrid: Boolean(documentModel.canvas?.snapToGrid),
      showGrid: Boolean(documentModel.canvas?.showGrid),
      breakpoint: documentModel.canvas?.breakpoint || 'desktop'
    };
  }

  private _createCanvasMetadata(): Record<string, QdsConfigField> {
    return {
      zoom: { displayName: 'Zoom', type: 'number', min: 50, max: 200, step: 10 },
      snapToGrid: { displayName: 'Snap To Grid', type: 'boolean' },
      showGrid: { displayName: 'Show Grid', type: 'boolean' },
      breakpoint: {
        displayName: 'Preview Breakpoint',
        type: 'select',
        options: BREAKPOINTS.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }))
      }
    };
  }

  private _createBlockMetaTarget(block: NonNullable<ReturnType<QdsContentProperties['_selectedBlock']>>): EditorTarget {
    return {
      title: block.title || '',
      hidden: Boolean(block.visibility?.hidden),
      responsive: (block.visibility?.responsive || BREAKPOINTS).join(', ')
    };
  }

  private _createBlockMetaMetadata(): Record<string, QdsConfigField> {
    return {
      title: { displayName: 'Label', type: 'string' },
      hidden: { displayName: 'Hidden', type: 'boolean' },
      responsive: {
        displayName: 'Responsive Breakpoints',
        description: 'Comma-separated values from desktop, tablet, mobile.',
        type: 'string'
      }
    };
  }

  private _createBlockLayoutTarget(block: NonNullable<ReturnType<QdsContentProperties['_selectedBlock']>>, mode: QdsContentDocument['mode']): EditorTarget {
    if (mode === 'dashboard') {
      return {
        x: block.layout?.x || 0,
        y: block.layout?.y || 0,
        w: block.layout?.w || 1,
        h: block.layout?.h || 1,
        minW: block.layout?.minW || 1,
        minH: block.layout?.minH || 1,
        maxW: block.layout?.maxW || 12,
        maxH: block.layout?.maxH || 12
      };
    }

    return {
      order: block.layout?.order || 0
    };
  }

  private _createBlockLayoutMetadata(mode: QdsContentDocument['mode']): Record<string, QdsConfigField> {
    if (mode === 'dashboard') {
      return {
        x: { displayName: 'X', type: 'number', min: 0, max: 12, step: 1 },
        y: { displayName: 'Y', type: 'number', min: 0, max: 50, step: 1 },
        w: { displayName: 'Width', type: 'number', min: 1, max: 12, step: 1 },
        h: { displayName: 'Height', type: 'number', min: 1, max: 20, step: 1 },
        minW: { displayName: 'Min Width', type: 'number', min: 1, max: 12, step: 1 },
        minH: { displayName: 'Min Height', type: 'number', min: 1, max: 20, step: 1 },
        maxW: { displayName: 'Max Width', type: 'number', min: 1, max: 12, step: 1 },
        maxH: { displayName: 'Max Height', type: 'number', min: 1, max: 20, step: 1 }
      };
    }

    return {
      order: { displayName: 'Order', type: 'number', min: 0, max: 200, step: 1 }
    };
  }

  private _createConfigTarget(
    block: NonNullable<ReturnType<QdsContentProperties['_selectedBlock']>>,
    definition: QdsBlockDefinition | undefined,
    includeHostEditorOnly = true
  ): { target: EditorTarget; metadata: Record<string, QdsConfigField> } {
    const target: EditorTarget = {};
    const metadata: Record<string, QdsConfigField> = {};
    this._configValueKinds = {};

    const keys = new Set<string>([
      ...Object.keys(definition?.defaultConfig || {}),
      ...Object.keys(block.config || {}),
      ...Object.keys(definition?.configSchema || {})
    ]);

    keys.forEach((key) => {
      const value = block.config[key];
      const field = getDefinitionField(definition, key);
      const usesStructuredEditor = Boolean(field?.editorTag && customElements.get(field.editorTag));
      if (field?.hostEditorOnly && !includeHostEditorOnly) {
        return;
      }
      if (field?.type === 'typography') {
        this._configValueKinds[key] = 'primitive';
        target[key] = value && typeof value === 'object' && !Array.isArray(value)
          ? { ...(value as Record<string, unknown>) }
          : undefined;
        metadata[key] = field;
        return;
      }

      const isJsonValue = Array.isArray(value) || (!!value && typeof value === 'object');
      if (usesStructuredEditor && isJsonValue) {
        this._configValueKinds[key] = 'structured';
        target[key] = cloneStructuredValue(value);
        metadata[key] = field || {
          displayName: key,
          type: 'textarea'
        };
        return;
      }

      this._configValueKinds[key] = isJsonValue ? 'json' : 'primitive';
      target[key] = isJsonValue ? JSON.stringify(value, null, 2) : value ?? '';
      metadata[key] = field || {
        displayName: key,
        type: typeof value === 'number'
          ? 'number'
          : typeof value === 'boolean'
            ? 'boolean'
            : 'string'
      };

      if (isJsonValue) {
        metadata[key] = {
          ...metadata[key],
          type: 'textarea',
          description: metadata[key].description || 'Edit this value as JSON.'
        };
      }
    });

    return { target, metadata };
  }

  private _bindPropertyEditor(
    id: string,
    label: string,
    target: EditorTarget,
    metadata: Record<string, QdsConfigField>,
    context: QdsDesignerContext
  ): void {
    const editor = this.shadowRoot?.getElementById(id) as any;
    if (!editor) {
      return;
    }

    editor.setAttribute('label', label);
    editor.target = target;
    editor.metadata = metadata;
    editor.context = context;
    editor.readonly = this._readonly;
  }

  private _renderReadonly(documentModel: QdsContentDocument, selectedBlock: ReturnType<QdsContentProperties['_selectedBlock']>, registry: QdsBlockRegistry): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .properties {
          display: grid;
          gap: 16px;
        }

        .properties__card {
          padding: 16px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 8px;
          background: var(--theme-background-primary, #ffffff);
        }

        .properties__card h3 {
          margin: 0 0 12px;
        }

        pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
        }
      </style>
      <section class="properties">
        <article class="properties__card">
          <h3>Document</h3>
          <pre>${escapeHtml(JSON.stringify(documentModel, null, 2))}</pre>
        </article>
        <article class="properties__card">
          <h3>${selectedBlock ? escapeHtml(getContentBlockDisplayName(selectedBlock, registry)) : 'No Block Selected'}</h3>
          <pre>${escapeHtml(JSON.stringify(selectedBlock || {}, null, 2))}</pre>
        </article>
      </section>
    `;
  }

  private _handleChange = (event: CustomEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target || !this._document) {
      return;
    }

    const registry = resolveRegistry(this._registry || this._context?.blockRegistry || null);
    const documentModel = normalizeContentDocument(this._document, registry);
    const selectedBlock = this._selectedBlock(documentModel);

    if (target.id === 'document-editor') {
      this._dispatch('document-change', {
        document: {
          ...(event.detail.target || {})
        }
      });
      return;
    }

    if (target.id === 'layout-editor') {
      this._dispatch('document-layout-change', {
        layout: {
          ...(event.detail.target || {})
        }
      });
      return;
    }

    if (target.id === 'canvas-editor') {
      this._dispatch('document-canvas-change', {
        canvas: {
          ...(event.detail.target || {})
        }
      });
      return;
    }

    if (!selectedBlock) {
      return;
    }

    if (target.id === 'block-meta-editor') {
      const responsiveSource = String(event.detail.target?.responsive || '');
      const responsive = responsiveSource
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value): value is QdsContentBreakpoint => BREAKPOINTS.includes(value as QdsContentBreakpoint));

      this._dispatch('block-meta-change', {
        blockId: selectedBlock.id,
        title: event.detail.target?.title || '',
        visibility: {
          hidden: Boolean(event.detail.target?.hidden),
          responsive: responsive.length > 0 ? responsive : BREAKPOINTS
        }
      });
      return;
    }

    if (target.id === 'block-layout-editor') {
      this._dispatch('block-layout-change', {
        blockId: selectedBlock.id,
        layout: {
          ...(event.detail.target || {})
        }
      });
      return;
    }

    if (target.id === 'block-config-editor') {
      const nextConfig: Record<string, unknown> = {};
      const source = event.detail.target || {};
      let hasParseError = false;
      let parseErrorMessage = '';

      Object.keys(source).forEach((key) => {
        const value = source[key];
        if (this._configValueKinds[key] === 'json') {
          if (typeof value !== 'string' || value.trim() === '') {
            nextConfig[key] = [];
            return;
          }

          try {
            nextConfig[key] = JSON.parse(value);
          } catch (error) {
            hasParseError = true;
            parseErrorMessage = `Invalid JSON for "${key}".`;
          }
          return;
        }

        nextConfig[key] = this._configValueKinds[key] === 'structured'
          ? cloneStructuredValue(value)
          : value;
      });

      if (hasParseError) {
        this._configParseError = parseErrorMessage;
        this.render();
        return;
      }

      this._configParseError = '';
      this._dispatch('block-config-change', {
        blockId: selectedBlock.id,
        config: nextConfig
      });
      return;
    }

    if (target.id === 'host-editor') {
      const detailConfig = event.detail?.config || event.detail?.value || (target as any).value;
      if (detailConfig && typeof detailConfig === 'object') {
        this._dispatch('block-config-change', {
          blockId: selectedBlock.id,
          config: detailConfig
        });
      }
    }
  };

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    if (!this._document) {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
          }
        </style>
        <div>No document is loaded.</div>
      `;
      return;
    }

    const registry = resolveRegistry(this._registry || this._context?.blockRegistry || null);
    const documentModel = normalizeContentDocument(this._document, registry);
    const selectedBlock = this._selectedBlock(documentModel);
    const selectedDefinition = selectedBlock ? registry.get(selectedBlock.type) : undefined;

    if (this._readonly) {
      this._renderReadonly(documentModel, selectedBlock, registry);
      return;
    }

    const documentTarget = this._createDocumentTarget(documentModel);
    const layoutTarget = this._createLayoutTarget(documentModel);
    const canvasTarget = this._createCanvasTarget(documentModel);
    const blockMetaTarget = selectedBlock ? this._createBlockMetaTarget(selectedBlock) : null;
    const editorContext = this._context || { blockRegistry: registry };
    const showBlockLayoutEditor = Boolean(selectedBlock && documentModel.mode === 'dashboard');
    const blockLayoutTarget = selectedBlock && showBlockLayoutEditor
      ? this._createBlockLayoutTarget(selectedBlock, documentModel.mode)
      : null;
    const showHostEditor = Boolean(selectedBlock && selectedDefinition?.editorTag && customElements.get(selectedDefinition.editorTag));
    const configBundle = selectedBlock
      ? this._createConfigTarget(selectedBlock, selectedDefinition, !showHostEditor)
      : null;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .properties {
          display: grid;
          gap: 12px;
        }

        qds-property-editor {
          --qds-property-editor-margin-block: 0;
        }

        .properties__host-editor {
          display: block;
        }

        .properties__error {
          margin: 0;
          font-size: 12px;
          color: #b3261e;
        }
      </style>
      <section class="properties">
        ${selectedBlock
          ? `
            ${showHostEditor ? `<${selectedDefinition?.editorTag} id="host-editor" class="properties__host-editor"></${selectedDefinition?.editorTag}>` : ''}
            <qds-property-editor id="block-meta-editor" expanded></qds-property-editor>
            ${showBlockLayoutEditor ? '<qds-property-editor id="block-layout-editor" expanded></qds-property-editor>' : ''}
            ${configBundle && Object.keys(configBundle.target).length > 0 ? `
              <qds-property-editor id="block-config-editor" expanded></qds-property-editor>
              ${this._configParseError ? `<p class="properties__error">${this._configParseError}</p>` : ''}
            ` : ''}
          `
          : ''}
        <qds-property-editor id="document-editor" expanded></qds-property-editor>
        <qds-property-editor id="layout-editor" expanded></qds-property-editor>
        <qds-property-editor id="canvas-editor" expanded></qds-property-editor>
      </section>
    `;

    this._bindPropertyEditor('document-editor', 'Document Settings', documentTarget, this._createDocumentMetadata(), editorContext);
    this._bindPropertyEditor('layout-editor', 'Layout Settings', layoutTarget, this._createLayoutMetadata(), editorContext);
    this._bindPropertyEditor('canvas-editor', 'Canvas Settings', canvasTarget, this._createCanvasMetadata(), editorContext);

    if (selectedBlock && blockMetaTarget) {
      this._bindPropertyEditor('block-meta-editor', 'Block Settings', blockMetaTarget, this._createBlockMetaMetadata(), editorContext);
    }

    if (selectedBlock && blockLayoutTarget) {
      this._bindPropertyEditor('block-layout-editor', 'Block Layout', blockLayoutTarget, this._createBlockLayoutMetadata(documentModel.mode), editorContext);
    }

    if (selectedBlock && configBundle && Object.keys(configBundle.target).length > 0) {
      this._bindPropertyEditor('block-config-editor', 'Block Configuration', configBundle.target, configBundle.metadata, editorContext);
    }

    if (showHostEditor) {
      const hostEditor = this.shadowRoot.getElementById('host-editor') as any;
      if (hostEditor) {
        hostEditor.block = selectedBlock;
        hostEditor.config = selectedBlock?.config;
        hostEditor.contentDocument = documentModel;
        hostEditor.context = editorContext;
        hostEditor.readonly = this._readonly;
      }
    }
  }
}

if (!customElements.get('qds-content-properties')) {
  customElements.define('qds-content-properties', QdsContentProperties);
}

export default QdsContentProperties;
